/* ============================================================
   L'ART DU CARRÉ — Synchronisation du compteur panier
   Fichier : assets/fc-cart-count.js
   ------------------------------------------------------------
   Problème corrigé :
   - le compteur de l'en-tête produit minimal (.hm__count) était
     rendu côté serveur et n'était jamais mis à jour après un
     ajout / une suppression ;
   - au retour arrière du navigateur (bfcache), la page était
     restituée telle quelle avec un compteur périmé ;
   - certaines apps modifient le panier sans prévenir le thème.

   Principe : toute mutation du panier déclenche une relecture de
   la source de vérité (rendu serveur de la section cart-icon-bubble,
   qui applique la règle « produits taggés cart-hidden exclus »),
   puis le chiffre exact est repeint dans TOUS les compteurs de la page.
   ============================================================ */
(function () {
  'use strict';

  if (window.__fcCartCount) return;
  window.__fcCartCount = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;

  var known = null;      // dernier compte connu
  var pending = null;    // promesse de rafraîchissement en cours
  var queued = false;    // une autre mutation est arrivée pendant le fetch
  var timers = [];

  /* ---------- lecture ---------- */

  function fetchRawCount() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) { return typeof c.item_count === 'number' ? c.item_count : null; })
      .catch(function () { return null; });
  }

  // Source de vérité : la section cart-icon-bubble rendue par Shopify,
  // qui retire déjà les articles « cart-hidden » (cadeaux, upsells).
  function fetchTrueCount() {
    return fetch(ROOT + '?sections=cart-icon-bubble', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data['cart-icon-bubble'];
        if (typeof html !== 'string') return fetchRawCount();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('.cart-count-bubble')) return 0;
        var digit = doc.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        var n = digit ? parseInt(digit.textContent.trim(), 10) : NaN;
        if (!isNaN(n)) return n;
        return fetchRawCount(); // bulle sans chiffre : 100 articles ou plus
      })
      .catch(function () { return fetchRawCount(); });
  }

  /* ---------- écriture ---------- */

  function label(n) {
    if (n === null) return '';
    return n > 99 ? '99+' : String(n);
  }

  // Bulle façon Dawn : <div class="cart-count-bubble"><span aria-hidden>n</span><span class="visually-hidden">…</span></div>
  function paintDawnBubble(host, n) {
    var bubble = host.querySelector('.cart-count-bubble');
    if (!n) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      host.appendChild(bubble);
    }
    var digit = bubble.querySelector('span[aria-hidden="true"]');
    if (!digit) {
      digit = document.createElement('span');
      digit.setAttribute('aria-hidden', 'true');
      bubble.insertBefore(digit, bubble.firstChild);
    }
    if (digit.textContent !== label(n)) digit.textContent = label(n);

    var sr = bubble.querySelector('.visually-hidden');
    if (!sr) {
      sr = document.createElement('span');
      sr.className = 'visually-hidden';
      bubble.appendChild(sr);
    }
    sr.textContent = n + (n > 1 ? ' articles' : ' article');
  }

  // Pastille de l'en-tête produit minimal : <span class="hm__count">n</span>
  function paintMiniBubble(host, n) {
    var pill = host.querySelector('.hm__count');
    if (!n) {
      if (pill) pill.hidden = true;
      return;
    }
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'hm__count';
      host.appendChild(pill);
    }
    pill.hidden = false;
    if (pill.textContent !== label(n)) pill.textContent = label(n);
  }

  function paint(n) {
    if (n === null || n === undefined) return;
    known = n;

    var dawn = document.querySelectorAll('#cart-icon-bubble, .header__icon--cart, [data-fc-cart-bubble]');
    for (var i = 0; i < dawn.length; i++) paintDawnBubble(dawn[i], n);

    var mini = document.querySelectorAll('[data-fc-cart-mini]');
    for (var j = 0; j < mini.length; j++) paintMiniBubble(mini[j], n);

    var plain = document.querySelectorAll('[data-cart-count]');
    for (var k = 0; k < plain.length; k++) {
      if (plain[k].textContent !== label(n)) plain[k].textContent = label(n);
    }

    // Étiquettes d'accessibilité des liens panier
    var links = document.querySelectorAll('a[href$="/cart"], a[href*="/cart?"]');
    for (var l = 0; l < links.length; l++) {
      if (links[l].hasAttribute('aria-label')) {
        links[l].setAttribute('aria-label', 'Panier (' + n + ' article' + (n > 1 ? 's' : '') + ')');
      }
    }

    document.dispatchEvent(new CustomEvent('fc:cart-count', { detail: { count: n } }));
  }

  // Le thème réécrit parfois #cart-icon-bubble juste après nous, avec une
  // valeur mise en cache. On repasse derrière pour avoir le dernier mot.
  function repaintLater() {
    timers.forEach(clearTimeout);
    timers = [350, 900, 1800].map(function (ms) {
      return setTimeout(function () { if (known !== null) paint(known); }, ms);
    });
  }

  /* ---------- orchestration ---------- */

  function refresh() {
    if (pending) { queued = true; return pending; }
    pending = fetchTrueCount()
      .then(function (n) {
        paint(n);
        repaintLater();
      })
      .finally(function () {
        pending = null;
        if (queued) { queued = false; refresh(); }
      });
    return pending;
  }

  var debounceId = null;
  function scheduleRefresh(delay) {
    clearTimeout(debounceId);
    debounceId = setTimeout(refresh, typeof delay === 'number' ? delay : 120);
  }

  /* ---------- détection des mutations ---------- */

  // fetch()
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var isMutation = MUTATION_RE.test(url);
      var p = nativeFetch.apply(this, arguments);
      if (isMutation) p.then(function () { scheduleRefresh(60); }, function () {});
      return p;
    };
  }

  // XMLHttpRequest (apps tierces)
  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { this.__fcCartMutation = MUTATION_RE.test(String(url)); } catch (e) {}
    return nativeOpen.apply(this, arguments);
  };
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__fcCartMutation) {
      this.addEventListener('loadend', function () { scheduleRefresh(60); });
    }
    return nativeSend.apply(this, arguments);
  };

  // Retour arrière / restitution depuis le cache navigateur
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refresh();
  });

  // Onglet réactivé (panier modifié ailleurs)
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleRefresh(200);
  });

  // Sections rechargées par l'éditeur de thème ou par le thème lui-même
  document.addEventListener('shopify:section:load', function () {
    if (known !== null) paint(known);
    scheduleRefresh(300);
  });

  // Évènements personnalisés émis par certaines apps / le thème
  ['cart:updated', 'cart:refresh', 'cart-update', 'cartUpdate'].forEach(function (evt) {
    document.addEventListener(evt, function () { scheduleRefresh(60); });
    window.addEventListener(evt, function () { scheduleRefresh(60); });
  });

  /* ---------- démarrage ---------- */

  function start() { refresh(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.FCCartCount = { refresh: refresh, get: function () { return known; } };
})();